import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Paper,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Divider
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FolderIcon from "@mui/icons-material/Folder";
import InventoryIcon from "@mui/icons-material/Inventory";
import AddIcon from "@mui/icons-material/Add";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  AlertBannerContext,
  API_RESPONSE_CODE,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { Category } from "./types/categoryTypes";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import AddCategoryModal from "./AddCategoryModal";
import { Product, GetProductListResponse } from "../Products/types/products";
import { ProductStatus } from "../Products/enums/products";
import { fetchGraphQL } from "../../Graphql/utils";
import { getProductListQuery } from "../../Graphql/querries/productQuery";

interface ViewSubcategoryDetailProps {
  subcategory: Category;
  parentCategory: Category;
  onBack: () => void;
  onEdit: (cat: Category) => void;
  onDelete: (cat: Category) => void;
}

const DEFAULT_PAGE_SIZE = 10;

const ViewSubcategoryDetail: React.FC<ViewSubcategoryDetailProps> = ({
  subcategory,
  parentCategory,
  onBack,
  onEdit,
  onDelete
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();
  const { setAlert } = useContext(AlertBannerContext);
  const [openAddNestedSubcategoryModal, setOpenAddNestedSubcategoryModal] =
    useState(false);
  const [nestedSubcategories, setNestedSubcategories] = useState<Category[]>(
    []
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [size] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);

  const { categoryList } = useSelector((state: RootState) => state.categories);

  useEffect(() => {
    const nested = categoryList.filter(
      (cat) => cat.parentId === subcategory.id
    );
    setNestedSubcategories(nested);
  }, [subcategory.id, categoryList]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response: GetProductListResponse = await fetchGraphQL(
          getProductListQuery,
          {
            input: {
              category: subcategory.id,
              page,
              size
            }
          },
          (setAlert ?? (() => {})) as (alert: {
            severity: string;
            code: string;
          }) => void
        );
        const { listProducts } = response;
        if (
          listProducts?.code === API_RESPONSE_CODE.OK &&
          listProducts.products
        ) {
          setProducts(listProducts.products);
          setTotalCount(
            listProducts.totalElements ?? listProducts.products.length
          );
        } else {
          setProducts([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error("Failed to fetch products:", error);
        setProducts([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [subcategory.id, page, size, setAlert]);

  const getStockStatus = (
    product: Product
  ): { label: string; color: "success" | "warning" | "error" } => {
    // Deterministic mock stock (derive from product.id) so UI and tests are stable until real API returns quantity
    const mockStock = product.id
      ? ((product.id.charCodeAt(0) + (product.id.charCodeAt(1) ?? 0)) % 80) + 21
      : 50;
    const threshold = 10;

    if (mockStock === 0) {
      return { label: t("out-of-stock", "Out of Stock"), color: "error" };
    } else if (mockStock <= threshold) {
      return { label: t("low-stock", "Low Stock"), color: "warning" };
    }
    return { label: t("in-stock", "In Stock"), color: "success" };
  };

  const getStatusColor = (
    status?: ProductStatus
  ): "success" | "warning" | "error" | "default" => {
    switch (status) {
      case ProductStatus.ACTIVE:
        return "success";
      case ProductStatus.INACTIVE:
        return "warning";
      case ProductStatus.DISCONTINUED:
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ minHeight: "calc(100vh - 110px)", mt: 1 }}>
      {/* Breadcrumb Navigation */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <Link
          component="button"
          variant="body2"
          onClick={onBack}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            textDecoration: "none",
            color: BRAND_COLOR,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" }
          }}
        >
          <HomeIcon fontSize="small" />
          {parentCategory.name}
        </Link>
        <Typography color="text.primary" variant="body2">
          {subcategory.name}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton
          onClick={onBack}
          aria-label={t("go-back", "Go back")}
          sx={{
            color: BRAND_COLOR,
            "&:hover": { backgroundColor: `${BRAND_COLOR}15` }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h5" fontWeight="bold">
            {subcategory.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              "subcategory-details-products",
              "Subcategory Details & Products"
            )}
          </Typography>
        </Box>
        <Stack direction={matchMD ? "column" : "row"} spacing={1}>
          <Button
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            onClick={() => onEdit(subcategory)}
            sx={{
              textTransform: "none",
              color: BRAND_COLOR,
              borderColor: BRAND_COLOR,
              "&:hover": {
                borderColor: BRAND_HOVER,
                backgroundColor: `${BRAND_COLOR}10`
              }
            }}
          >
            {t("edit", "Edit")}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineOutlinedIcon />}
            onClick={() => onDelete(subcategory)}
            sx={{ textTransform: "none" }}
          >
            {t("delete", "Delete")}
          </Button>
        </Stack>
      </Box>

      {/* Subcategory Information */}
      <Card sx={{ mb: 3, boxShadow: theme.shadows[2] }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={2}>
            {t("subcategory-information", "Subcategory Information")}
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("description", "Description")}
              </Typography>
              <Typography variant="body1">
                {subcategory.description ||
                  t("no-description-provided", "No description provided")}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("status", "Status")}
              </Typography>
              <Chip
                label={subcategory.status || "ACTIVE"}
                color={
                  subcategory.status?.toLowerCase() === "active"
                    ? "success"
                    : "default"
                }
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("full-path", "Full Path")}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", color: "text.primary" }}
              >
                {subcategory.path ||
                  `/${parentCategory.name.toLowerCase()}/${subcategory.name.toLowerCase()}`}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("depth-level", "Depth Level")}
              </Typography>
              <Chip
                label={`${t("level", "Level")} ${subcategory.depth || 1}`}
                size="small"
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("products", "Products")}
              </Typography>
              <Typography variant="h6" color="primary">
                {loading ? "—" : totalCount}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t("nested-categories", "Nested Categories")}
              </Typography>
              <Typography variant="h6" color="primary">
                {nestedSubcategories.length}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Nested Subcategories Section */}
      {(subcategory.depth || 1) < 5 && (
        <Box mb={3}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" fontWeight={600}>
              {t("nested-subcategories", "Nested Subcategories")} (
              {nestedSubcategories.length})
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddNestedSubcategoryModal(true)}
              sx={{
                textTransform: "none",
                color: BRAND_COLOR,
                borderColor: BRAND_COLOR,
                "&:hover": {
                  borderColor: BRAND_HOVER,
                  backgroundColor: `${BRAND_COLOR}10`
                }
              }}
            >
              {t("add-nested-category", "Add Nested Category")}
            </Button>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress sx={{ color: BRAND_COLOR }} />
            </Box>
          ) : nestedSubcategories.length === 0 ? (
            <Alert severity="info">
              {t(
                "no-nested-categories",
                "No nested categories. You can add deeper categorization if needed."
              )}
            </Alert>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ boxShadow: theme.shadows[1], mb: 3 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("category-name", "Category Name")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("products", "Products")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("status", "Status")}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      {t("actions", "Actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nestedSubcategories.map((nested) => (
                    <TableRow key={nested.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <FolderIcon color="primary" fontSize="small" />
                          <Typography variant="body2">{nested.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {nested.productCount || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={nested.status || "ACTIVE"}
                          color="success"
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <IconButton
                            size="small"
                            onClick={() => onEdit(nested)}
                            color="primary"
                            aria-label={t("edit", "Edit")}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => onDelete(nested)}
                            color="error"
                            aria-label={t("delete", "Delete")}
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Products Section */}
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" fontWeight={600}>
            {t("products", "Products")} ({loading ? "—" : totalCount})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER },
              textTransform: "none"
            }}
            onClick={() => {
              console.log("Add product to subcategory:", subcategory.id);
            }}
          >
            {t("add-product", "Add Product")}
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress sx={{ color: BRAND_COLOR }} />
          </Box>
        ) : products.length === 0 ? (
          <Alert severity="info">
            {t(
              "no-products-in-subcategory",
              'No products in this subcategory. Click "Add Product" to add one.'
            )}
          </Alert>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ boxShadow: theme.shadows[1] }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("product-name", "Product Name")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("selling-mode", "Selling Mode")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("price", "Price")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("stock-status", "Stock Status")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("status", "Status")}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    {t("actions", "Actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => {
                  const stockStatus = getStockStatus(product);
                  return (
                    <TableRow key={product.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <InventoryIcon color="action" fontSize="small" />
                          <Box>
                            <Typography fontWeight={500}>
                              {product.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {product.baseUnit ?? product.unit ?? "—"}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.sellingMode}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          ${product.minSalesPrice?.toFixed(2) || "0.00"}
                        </Typography>
                        {product.purchasePrice && (
                          <Typography variant="caption" color="text.secondary">
                            {t("cost", "Cost")}: $
                            {product.purchasePrice.toFixed(2)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={stockStatus.label}
                          size="small"
                          color={stockStatus.color}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.status || ProductStatus.ACTIVE}
                          size="small"
                          color={getStatusColor(product.status)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label={t("view-product", "View product")}
                            onClick={() => {
                              console.log("View product:", product.id);
                            }}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label={t("edit-product", "Edit product")}
                            onClick={() => {
                              console.log("Edit product:", product.id);
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={t("delete-product", "Delete product")}
                            onClick={() => {
                              console.log("Delete product:", product.id);
                            }}
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && totalCount > size && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              variant="outlined"
              count={Math.max(1, Math.ceil(totalCount / size))}
              page={page + 1}
              onChange={(_e, p) => setPage(p - 1)}
              shape="rounded"
              color="primary"
            />
          </Box>
        )}
      </Box>

      {/* Add Nested Subcategory Modal */}
      <AddCategoryModal
        open={openAddNestedSubcategoryModal}
        onClose={() => setOpenAddNestedSubcategoryModal(false)}
        initialParentId={subcategory.id}
      />
    </Box>
  );
};

export default ViewSubcategoryDetail;
